/**
 * jersey-customizations.js
 *
 * JerseyCustomization — Custom Element for the jersey printing/personalisation UI.
 *
 * UX flow:
 *   1. Toggle: "Ei painatusta" / "Lisää painatus"
 *   2. If Lisää painatus: one unified card appears with
 *      - Name input (required)
 *      - Number input (required, numeric only)
 *      - Captain mark: Ei / C / A radio pills (optional)
 *      - Live jersey preview
 *
 * Variant sync: maps UI state to the native variant-selects Printing/Painatus option
 *   none → NONE variant
 *   yes + captain Ei → NAMENUM variant
 *   yes + captain C or A → BOTH variant
 */

class JerseyCustomization extends HTMLElement {
  /** 'none' | 'yes' */
  #state = { printing: 'none' };
  #abort = new AbortController();

  // ─── Lifecycle ─────────────────────────────────────────────────────────

  connectedCallback() {
    this.#bindEvents();
    this.#initVariantSync();

    if (!this.#getCurrentNativeValue()) {
      const map = this.#buildValueMap();
      if (map.NONE) this.#setNativeValue(map.NONE);
    }

    this.#syncFromVariant();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /**
   * Always do a fresh DOM lookup so we're never holding a stale reference
   * to a variant-selects element that was replaced after a size change.
   */
  get #vr() {
    return document.querySelector(
      `variant-selects[data-section="${this.dataset.section}"][data-product-id="${this.dataset.product}"]`
    );
  }

  // ─── Event binding ─────────────────────────────────────────────────────

  #bindEvents() {
    const opts = { signal: this.#abort.signal };

    // Step 1 toggle
    this.querySelector('.jc-step--1')?.addEventListener('change', e => {
      if (e.target.type === 'radio') this.#onToggleChange(e.target.value);
    }, opts);

    // Captain radio pills — re-sync variant when selection changes
    this.querySelector('.jc-inputs')?.addEventListener('change', e => {
      if (e.target.classList.contains('jc-captain-radio')) this.#onCaptainChange();
    }, opts);

    // Live preview
    this.querySelector('.jc-name-input')
      ?.addEventListener('input', () => this.#updatePreview(), opts);
    this.querySelector('.jc-number-input')
      ?.addEventListener('input', () => this.#updatePreview(), opts);
  }

  #initVariantSync() {
    // Listen on the product-form's hidden variant ID input.
    // updateVariantInput() in VariantSelects dispatches 'change' on it after every
    // variant fetch — this tells us when the size (or other option) changed externally.
    const form = document.getElementById(`product-form-${this.dataset.section}`);
    form?.querySelector('input[name="id"]')?.addEventListener('change', () => {
      Promise.resolve().then(() => this.#syncFromVariant());
    }, { signal: this.#abort.signal });
  }

  // ─── User interactions ──────────────────────────────────────────────────

  #onToggleChange(value) {
    if (value === 'none') {
      this.#clearFields();
    }
    // value is 'none' | 'namenum' | 'captain'
    this.#setState(value);
  }

  #onCaptainChange() {
    this.#syncCaptainHidden();
    this.#syncToVariant();
  }

  // ─── State ─────────────────────────────────────────────────────────────

  #setState(printing) {
    this.#state = { printing };
    this.#render();
    this.#syncToVariant();
  }

  // ─── Rendering ─────────────────────────────────────────────────────────

  #render() {
    const { printing } = this.#state;
    const isNone       = printing === 'none';
    const isCaptain    = printing === 'captain';

    // Expand / collapse the card
    const inputsEl = this.querySelector('.jc-inputs');
    if (inputsEl) {
      inputsEl.classList.toggle('is-open', !isNone);
      inputsEl.inert = isNone;
    }

    // Name + number row: hidden (and inputs disabled) in captain-only mode
    const nameRow     = this.querySelector('.jc-inputs__row');
    const nameInput   = this.querySelector('.jc-name-input');
    const numberInput = this.querySelector('.jc-number-input');
    if (nameRow) nameRow.hidden = isCaptain;
    if (nameInput)   nameInput.disabled = isNone || isCaptain;
    if (numberInput) numberInput.disabled = isNone || isCaptain;

    // "Ei"-captain pill: hidden in captain-only mode (C or A becomes required)
    const captainNonePill = this.querySelector('.jc-captain-pill--none');
    if (captainNonePill) captainNonePill.hidden = isCaptain;

    // "Valinnainen"-badge: hidden when captain mark is required
    const optBadge = this.querySelector('.jc-optional-badge');
    if (optBadge) optBadge.hidden = isCaptain;

    // Ensure a valid captain is pre-selected when switching to captain-only
    if (isCaptain) {
      const checked = this.querySelector('.jc-captain-radio:checked');
      if (!checked?.value) {
        const first = this.querySelector('.jc-captain-radio[value="C"]');
        if (first) first.checked = true;
      }
    }

    this.#syncCaptainHidden();
    this.#updatePreview();
  }

  /**
   * Keep the hidden form input (`name="properties[Captain badge]"`) in sync
   * with the UI radio selection. Disabled when "Ei" is selected so no empty
   * property appears on the order.
   */
  #syncCaptainHidden() {
    const captainHidden = this.querySelector('.jc-captain-value');
    if (!captainHidden) return;
    const { printing } = this.#state;
    const val = this.#getCaptainValue();
    captainHidden.value = val;
    // Disabled when: no printing, or namenum without a captain selection
    captainHidden.disabled = printing === 'none' || (printing !== 'captain' && !val);
  }

  // ─── Preview ───────────────────────────────────────────────────────────

  #updatePreview() {
    const { printing } = this.#state;
    const nameInput   = this.querySelector('.jc-name-input');
    const numberInput = this.querySelector('.jc-number-input');

    if (nameInput) {
      nameInput.value = nameInput.value
        .replace(/[^a-zA-ZåäöÅÄÖ\- .]/g, '')
        .toUpperCase();
    }

    if (numberInput) {
      numberInput.value = numberInput.value.replace(/\D/g, '').slice(0, 2);
    }

    const previewName   = this.querySelector('.jc-preview__name');
    const previewNumber = this.querySelector('.jc-preview__number');
    const showNameNumber = printing === 'namenum';
    if (previewName)   previewName.textContent   = showNameNumber ? (nameInput?.value ?? '') : '';
    if (previewNumber) previewNumber.textContent = showNameNumber ? (numberInput?.value ?? '') : '';
  }

  #clearFields() {
    const nameInput   = this.querySelector('.jc-name-input');
    const numberInput = this.querySelector('.jc-number-input');
    if (nameInput)   nameInput.value   = '';
    if (numberInput) numberInput.value = '';
    // Reset captain pill back to "Ei" when clearing
    const noneRadio = this.querySelector('.jc-captain-radio[value=""]');
    if (noneRadio) noneRadio.checked = true;
    this.#updatePreview();
  }

  #getCaptainValue() {
    return this.querySelector('.jc-captain-radio:checked')?.value ?? '';
  }

  // ─── Variant sync helpers ───────────────────────────────────────────────

  #norm(s) {
    return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  #getPrintingFieldset() {
    if (!this.#vr) return null;
    const byAttr = this.#vr.querySelector(
      'fieldset[data-option-name="Painatus"], fieldset[data-option-name="Print"]'
    );
    if (byAttr) return byAttr;
    const all = Array.from(this.#vr.querySelectorAll('fieldset'));
    return all.find(f => {
      const text = (f.querySelector('legend')?.textContent || '').toLowerCase();
      return text.includes('painatus') || text.includes('print');
    }) ?? all[all.length - 1] ?? null;
  }

  #getNativeInputs() {
    const fs = this.#getPrintingFieldset();
    if (!fs) return { radios: [], select: null, optionName: '' };
    // Woolman's product-variant-options uses name="{{ option.name }}" (e.g. "Painatus"),
    // NOT "options[Painatus]". These inputs also carry data-option-value-id which
    // VariantSelects.getSelectedOptionValues() needs for its AJAX fetch.
    const optionName = fs.getAttribute('data-option-name') || '';
    return {
      radios:     Array.from(fs.querySelectorAll(`input[type="radio"][name="${optionName}"]`)),
      select:     fs.querySelector(`select[name="${optionName}"]`),
      optionName,
    };
  }

  #getAllNativeValues() {
    const { radios, select } = this.#getNativeInputs();
    if (radios.length) return radios.map(r => r.value.trim());
    if (select) return Array.from(select.options).map(o => o.value.trim());
    return [];
  }

  #buildValueMap() {
    const vals  = this.#getAllNativeValues();
    const norms = vals.map(v => this.#norm(v));
    const pick  = synonyms => {
      for (let i = 0; i < norms.length; i++) {
        if (synonyms.includes(norms[i])) return vals[i];
      }
      for (let i = 0; i < norms.length; i++) {
        if (synonyms.some(s => norms[i].includes(s))) return vals[i];
      }
      return null;
    };
    return {
      NONE:    pick(['ei painatusta', 'no printing', 'no print', 'ei', 'none', 'no']),
      NAMENUM: pick(['nimi ja numero', 'name and number', 'name & number', 'nimi+numero', 'custom name & number']),
      CAPTAIN: pick(['kapteeninmerkki', 'kapteenin merkki', 'captain mark']),
      BOTH:    pick(['molemmat', 'both', 'printing both', 'painatus molemmat']),
    };
  }

  #getCurrentNativeValue() {
    const { radios, select } = this.#getNativeInputs();
    if (radios.length) return radios.find(r => r.checked)?.value ?? null;
    if (select) return select.value || null;
    return null;
  }

  // ─── Syncing to variant ─────────────────────────────────────────────────

  #syncToVariant() {
    const map      = this.#buildValueMap();
    const { printing } = this.#state;

    let target;
    if (printing === 'none') {
      target = map.NONE;
    } else if (printing === 'captain') {
      // Captain-only: always use the CAPTAIN variant regardless of C/A badge
      target = map.CAPTAIN ?? map.BOTH;
    } else {
      // namenum: captain C or A selected → BOTH variant; otherwise name+number only
      const hasCaptain = !!this.#getCaptainValue();
      target = hasCaptain
        ? (map.BOTH    ?? map.NAMENUM)
        : (map.NAMENUM ?? map.BOTH);
    }

    if (target) this.#setNativeValue(target);
  }

  #setNativeValue(value) {
    if (!value) return;
    const { radios, select } = this.#getNativeInputs();

    if (radios.length) {
      const target = radios.find(r => this.#norm(r.value) === this.#norm(value));
      if (!target) {
        console.warn('[jersey-customization] radio not found for value:', value,
          '| available:', radios.map(r => r.value));
        return;
      }
      // Don't re-trigger an AJAX fetch if the value hasn't actually changed
      if (target.checked) return;
      radios.forEach(r => { r.checked = r === target; });
      // Bubbles up to variant-selects → onVariantChange() → AJAX fetch → price update
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (select) {
      if (this.#norm(select.value) === this.#norm(value)) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ─── Syncing from variant ───────────────────────────────────────────────

  #syncFromVariant() {
    const map = this.#buildValueMap();
    const cur = this.#norm(this.#getCurrentNativeValue() || '');
    const n   = v => this.#norm(v || '');

    // Map current variant value back to our three UI states
    let printing;
    if (!cur || (map.NONE && cur === n(map.NONE))) {
      printing = 'none';
    } else if (map.CAPTAIN && cur === n(map.CAPTAIN)) {
      printing = 'captain';
    } else {
      printing = 'namenum';
    }

    // Sync toggle radios
    this.querySelectorAll('.jc-step--1 input[type="radio"]').forEach(r => {
      r.checked = r.value === printing;
    });

    // Sync captain pills
    if (printing === 'namenum') {
      const isBoth = map.BOTH && cur === n(map.BOTH);
      if (isBoth) {
        // Prefer whatever is already checked; if nothing, default to C
        const captainRadios = Array.from(this.querySelectorAll('.jc-captain-radio'));
        if (!captainRadios.some(r => r.value && r.checked)) {
          const c = captainRadios.find(r => r.value === 'C');
          if (c) c.checked = true;
        }
      } else {
        // NAMENUM only: reset captain to "Ei"
        const noneRadio = this.querySelector('.jc-captain-radio[value=""]');
        if (noneRadio) noneRadio.checked = true;
      }
    }

    this.#state = { printing };
    this.#render();
  }
}

if (!customElements.get('jersey-customization')) {
  customElements.define('jersey-customization', JerseyCustomization);
}
